import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  invalidateEntityQueries,
  invalidateQueriesWithActiveTasks,
  queryClient,
  toEntityChangeData,
} from '../lib/queryClient';
import { invalidateQueriesAwaitingJobs, noteRunPush } from '../lib/jobStateInvalidation';
import { publishSyncSignal } from '../lib/syncSignals';
import { apiService } from '../services/api.service';
import { useEntityActivityStore } from '../stores/entityActivityStore';
import { isValidTaskSignal } from '../utils/entityActivity';
import { useAuth } from './AuthContext';
import { useWorkspaceId } from './WorkspaceContext';

/**
 * Socket.io Context
 * Manages WebSocket connections for real-time features:
 * - Data sync (triggers TanStack Query invalidation)
 * - Push notifications
 */

// Socket namespaces
type SocketNamespace = 'sync' | 'notifications';

interface SocketContextType {
  // Connection state
  isConnected: boolean;
  connectionError: string | null;

  // Namespace sockets
  syncSocket: Socket | null;
  notificationsSocket: Socket | null;

  // Manual control (usually not needed)
  connect: () => void;
  disconnect: () => void;

  // Get a specific namespace socket
  getSocket: (namespace: SocketNamespace) => Socket | null;
}

const SocketContext = createContext<SocketContextType | null>(null);

/**
 * Hook to get a specific namespace socket
 */
export const useNamespaceSocket = (namespace: SocketNamespace): Socket | null => {
  const context = useContext(SocketContext);
  return context ? context.getSocket(namespace) : null;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

import { API_URL } from '../config/env';

// Get the API URL for socket connections
const getSocketUrl = (): string => API_URL;

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { user, getAuthToken } = useAuth();
  const workspaceId = useWorkspaceId();

  // Socket instances for each namespace
  const syncSocketRef = useRef<Socket | null>(null);
  const notificationsSocketRef = useRef<Socket | null>(null);

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Track if we're currently connecting to prevent duplicate connections
  const isConnectingRef = useRef(false);
  const isConnectedRef = useRef(false);
  // Guard against repeatedly force-refreshing on every reconnect attempt.
  // Reset when a connect succeeds, or when disconnect is called explicitly.
  const authRefreshAttemptedRef = useRef(false);
  // True once the sync socket has connected at least once. Distinguishes the
  // first connect (nothing to catch up on) from a later reconnect (socket.io's
  // own silent retry, or our explicit workspace-change reconnect), when
  // whatever changed while we were offline needs a targeted refetch.
  const wasConnectedRef = useRef(false);

  /**
   * Create a socket connection to a namespace.
   *
   * `auth` is the function form so socket.io re-invokes it on every
   * connection attempt (initial + each reconnect). The static-object
   * form captured the token once at `io()` time, which meant a token
   * that expired between the page-load fetch and a reconnect attempt
   * kept getting replayed with the same stale value — sockets
   * exhausted `reconnectionAttempts` and went silent (incident
   * 2026-05-16). Refetching via `getAuthToken` lets the Firebase SDK
   * return whatever the api-service's refresh path has just installed
   * in its internal cache.
   *
   * Resilience: if `getAuthToken()` rejects, the callback would never
   * fire and the handshake would hang until `timeout` (20s) — visible
   * to the user as silent sync. A `.catch` sends an empty token so the
   * backend rejects cleanly and socket.io advances its reconnect loop.
   */
  const createNamespaceSocket = useCallback(
    (namespace: SocketNamespace, wsId: string): Socket => {
      const socketUrl = getSocketUrl();

      return io(`${socketUrl}/${namespace}`, {
        auth: (cb) => {
          getAuthToken()
            .then((freshToken) => {
              cb({ token: freshToken ?? '', workspaceId: wsId });
            })
            .catch(() => {
              cb({ token: '', workspaceId: wsId });
            });
        },
        // Connection options
        transports: ['websocket', 'polling'],
        reconnection: true,
        // Never give up. A cap of 5 at 1-5 s backoff gave up after ~17 s, so
        // a longer outage left the socket dead until reload and the reconnect
        // catch-up below never ran (staging, 2026-09-30). Only transport
        // failures retry here: an auth rejection from the namespace
        // middleware destroys the socket, so an expired token cannot loop.
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });
    },
    [getAuthToken],
  );

  /**
   * Connect to all namespaces
   */
  const connect = useCallback(async () => {
    // Guard against duplicate connections
    if (isConnectingRef.current) {
      return;
    }

    // Need auth and workspace
    if (!user || !workspaceId) {
      return;
    }

    isConnectingRef.current = true;
    setConnectionError(null);
    authRefreshAttemptedRef.current = false;

    /**
     * If the handshake fails because the token is expired/invalid AND
     * no HTTP request has raced ahead to trigger a refresh, the socket
     * will keep replaying the same Firebase-cached token until it
     * exhausts `reconnectionAttempts`. Detect auth-shaped failures and
     * trigger one force-refresh via apiService.refreshAuth — that
     * shares the same in-flight promise the REST 401 path uses, so we
     * never duplicate a refresh, and it updates Firebase's internal
     * cache so the next reconnect's `auth` callback returns a fresh
     * token. Guarded to fire at most once per connect cycle (network
     * errors shouldn't keep retrying refresh).
     */
    const handleAuthFailureForRecovery = (message: string): void => {
      if (authRefreshAttemptedRef.current) return;
      if (!/auth|token/i.test(message)) return;
      authRefreshAttemptedRef.current = true;
      void apiService.refreshAuth().catch(() => {
        // refresh failed. A revoked or disabled session is signed out
        // by Firebase itself inside getIdToken; the other session-lost
        // codes are signed out by apiService's 401 path the next time
        // an HTTP request fires. A transient failure (Firebase outage,
        // offline) signs out nowhere. Don't pile on here.
      });
    };

    try {
      // Create sockets for each namespace. The `auth` function inside
      // `createNamespaceSocket` fetches the token lazily on each
      // (re)connect attempt, so no upfront fetch is needed here.
      const syncSocket = createNamespaceSocket('sync', workspaceId);
      const notificationsSocket = createNamespaceSocket('notifications', workspaceId);

      // Setup event handlers for sync socket
      syncSocket.on('connect', () => {
        console.debug('[Socket] Sync namespace connected', { socketId: syncSocket.id });
        setIsConnected(true);
        authRefreshAttemptedRef.current = false;
        // A RE-connect (not the first connect): the socket was silently
        // offline for a stretch, so entities with in-flight background tasks
        // may have moved without us seeing the /sync event. Refetch just
        // those, not everything: no polling, no interval.
        if (wasConnectedRef.current) {
          const staleIds = Object.keys(useEntityActivityStore.getState().tasks);
          // Remove EVERY entry, not only the active ones, so a refetched row
          // decides, not the stale event: a missed terminal event (task
          // finished, or failed and then retried from elsewhere, while the
          // socket was down) must not strand the loader on "running", and a
          // `failed`/`ready` entry must not outlive the reconnect either — a
          // retry finishing on another device must not leave this tab's tile
          // stuck on "failed" once it reconnects.
          useEntityActivityStore.getState().clearTasks(staleIds);
          staleIds.forEach((id) =>
            invalidateEntityQueries('artifact', 'updated', id, { workspaceId }),
          );
          // The store only knows ids an event named. A detail page reached
          // by reload or deep link, a chat intake, and the project children
          // list (which the call above cannot reach: it carries no
          // projectId) read their task from cached rows, so refetch every
          // cached query whose rows still show a queued or running task.
          invalidateQueriesWithActiveTasks();
        }
        wasConnectedRef.current = true;
      });
      syncSocket.on('disconnect', (reason) => {
        console.debug('[Socket] Sync namespace disconnected:', reason);
        setIsConnected(false);
      });
      syncSocket.on('connect_error', (error) => {
        console.error('[Socket] Sync connection error:', error.message);
        setConnectionError(error.message);
        setIsConnected(false);
        handleAuthFailureForRecovery(error.message);
      });

      // Listen for sync:connected confirmation from server (confirms room subscription)
      syncSocket.on('sync:connected', (data: { workspaceId: string; userId: string }) => {
        console.debug('[Socket] ✅ Sync room joined successfully', {
          workspaceId: data.workspaceId,
          userId: data.userId,
          socketId: syncSocket.id,
        });
        // EVERY join, the first included: a job that finished between a page's
        // first read and this join sent its event to a room we were not in
        // yet, and nothing polls any more to notice. Which queries refetch is
        // in lib/jobStateInvalidation.ts (import lists always, the other job
        // queries while they show work in flight).
        invalidateQueriesAwaitingJobs(queryClient);
        publishSyncSignal({ kind: 'joined', workspaceId: data.workspaceId });
      });

      // Handle sync events - invalidate TanStack Query caches
      const handleSyncEvent = (wsEvent: {
        entityType: string;
        changeType: string;
        entityId: string;
        workspaceId: string;
        timestamp: string;
        data?: Record<string, unknown>;
      }) => {
        console.debug('[Socket] Sync event received:', {
          entityType: wsEvent.entityType,
          changeType: wsEvent.changeType,
          entityId: wsEvent.entityId,
        });

        // A background-task state change rides the same broadcast. Feed it to
        // the activity store before invalidating, so any surface rendering
        // this entity right now sees the new state without waiting on the
        // query refetch below.
        const rawTask = (wsEvent.data as { task?: unknown } | undefined)?.task;
        if (isValidTaskSignal(rawTask) && wsEvent.entityId) {
          useEntityActivityStore.getState().setTask(wsEvent.entityId, rawTask);
        }

        // A run change an open run page will render: the `seen` clock point.
        noteRunPush(queryClient, wsEvent);

        // Invalidate TanStack Query caches - this triggers automatic refetch
        invalidateEntityQueries(
          wsEvent.entityType,
          wsEvent.changeType as 'created' | 'updated' | 'deleted',
          wsEvent.entityId,
          // Both ends of a move (the parent it left, too), so the source
          // folder's list refreshes as well as the target's.
          toEntityChangeData(wsEvent.data, wsEvent.workspaceId),
        );
        if (wsEvent.entityId) publishSyncSignal({ kind: 'entity', entityId: wsEvent.entityId });
      };

      syncSocket.on('entity:created', handleSyncEvent);
      syncSocket.on('entity:updated', handleSyncEvent);
      syncSocket.on('entity:deleted', handleSyncEvent);
      syncSocket.on('bulk:change', (data: { changes: Array<any> }) => {
        data.changes.forEach(handleSyncEvent);
      });

      // Setup event handlers for notifications socket
      notificationsSocket.on('connect', () => {
        console.debug('[Socket] Notifications namespace connected');
      });
      notificationsSocket.on('connect_error', (error) => {
        console.error('[Socket] Notifications connection error:', error.message);
        handleAuthFailureForRecovery(error.message);
      });

      // Store socket references
      syncSocketRef.current = syncSocket;
      notificationsSocketRef.current = notificationsSocket;
    } catch (error) {
      console.error('[Socket] Failed to connect:', error);
      setConnectionError(error instanceof Error ? error.message : 'Connection failed');
    } finally {
      isConnectingRef.current = false;
    }
  }, [user, workspaceId, createNamespaceSocket]);

  /**
   * Disconnect all sockets
   */
  const disconnect = useCallback(() => {
    if (syncSocketRef.current) {
      syncSocketRef.current.disconnect();
      syncSocketRef.current = null;
    }
    if (notificationsSocketRef.current) {
      notificationsSocketRef.current.disconnect();
      notificationsSocketRef.current = null;
    }
    setIsConnected(false);
    setConnectionError(null);
    authRefreshAttemptedRef.current = false;
    // A workspace switch tears down and reconnects. Without this reset the
    // next connect's "first connect" would still read as a reconnect and run
    // catch-up for the PREVIOUS workspace's active ids, which no longer mean
    // anything here.
    wasConnectedRef.current = false;
    useEntityActivityStore.setState({ tasks: {} });
  }, []);

  /**
   * Get socket by namespace
   */
  const getSocket = useCallback((namespace: SocketNamespace): Socket | null => {
    switch (namespace) {
      case 'sync':
        return syncSocketRef.current;
      case 'notifications':
        return notificationsSocketRef.current;
      default:
        return null;
    }
  }, []);

  // Auto-connect when user and workspace are available
  useEffect(() => {
    if (user && workspaceId) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [user, workspaceId, connect, disconnect]);

  // Wake a socket socket.io will never retry by itself: one the server rejected
  // in middleware (an expired token), which socket.io destroys, leaving it
  // inactive. An ACTIVE socket is left alone even while disconnected: its
  // manager is already retrying (attempts are unlimited), and connect() during
  // its handshake sends a second CONNECT, so the server joins it to the room
  // twice and every broadcast arrives twice. Both events are user-paced, so
  // this cannot storm. Reads the refs at event time, so after a workspace
  // switch it only touches the current sockets.
  useEffect(() => {
    const reconnectIfDown = () => {
      if (document.visibilityState !== 'visible') return;
      for (const socket of [syncSocketRef.current, notificationsSocketRef.current]) {
        if (socket && !socket.active) socket.connect();
      }
    };
    window.addEventListener('online', reconnectIfDown);
    document.addEventListener('visibilitychange', reconnectIfDown);
    return () => {
      window.removeEventListener('online', reconnectIfDown);
      document.removeEventListener('visibilitychange', reconnectIfDown);
    };
  }, []);

  // Keep isConnectedRef in sync with state (avoids adding isConnected as dep)
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  // Reconnect when workspace changes
  useEffect(() => {
    if (user && workspaceId && isConnectedRef.current) {
      // Workspace changed - reconnect with new workspace ID
      disconnect();
      connect();
    }
  }, [workspaceId, user, connect, disconnect]);

  const value: SocketContextType = {
    isConnected,
    connectionError,
    syncSocket: syncSocketRef.current,
    notificationsSocket: notificationsSocketRef.current,
    connect,
    disconnect,
    getSocket,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
